<template>
  <div class="business-login-form business-login-form__form-wrapper">
    <form class="business-login-form__form" @submit.prevent="validateFields">
      <Title
        header="Sign in to your INE account"
        :tagline="`Sign in to join ${invitationCompany}'s account.`"
      />
      <p class="business-login-form__with-email" v-if="invitationEmail">
        Enter you password for {{ invitationEmail }}
      </p>
      <Errors :errors="errors.messages" />
      <div
        v-for="(field, key) in fields"
        v-show="field.type"
        :key="`field-${key}`"
        class="business-login-form__form-group"
      >
        <input
          v-if="field.type && !field.hidden"
          :type="field.type"
          :class="[
            'ine-input business-login-form__form-control',
            errors.fields.includes(key) &&
              'business-login-form__form-control--error',
          ]"
          @focus="clearErrors"
          :placeholder="field.placeholder"
          :autocomplete="field.autocomplete"
          v-model="field.value"
        />
      </div>

      <div class="business-login-form__actions">
        <div class="business-login-form__actions__group">
          <router-link :to="{ name: 'forgot-password' }">
            <button class="btn btn--tertiary">
              Forgot your password?
            </button>
          </router-link>
          <SubmitBtn
            class="business-login-form__actions__submit-wrapper"
            extraButtonClasses="business-login-form__actions__submit"
            title="Sign In"
            :isLoading="isLoading"
          />
        </div>
        <div class="business-login-form__oauth">
          <span class="business-login-form__oauth--separator">OR</span>
        </div>
        <button
          v-for="provider in authProviders"
          :key="`provider-${provider.id}`"
          class="btn btn--secondary btn--default-icon-color"
          @click.prevent="$emit('login-through-provider', provider)"
          :disabled="isLoading"
        >
          <GoogleIcon class="business-login-form__oauth__icon" /> Log In with
          {{ provider.name }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { validateForm, clearFormErrors } from '@/helpers'
import Title from '@/components/forms/elements/Title.vue'
import Errors from '@/components/forms/elements/Errors.vue'
import SubmitBtn from '@/components/forms/elements/SubmitBtn.vue'
import GoogleIcon from '@/assets/icons/fill/google.svg'

export default {
  name: 'LoginForm',
  components: {
    Title,
    Errors,
    SubmitBtn,
    GoogleIcon,
  },
  data() {
    return {
      errors: clearFormErrors(),
      fields: {
        email: {
          type: 'email',
          placeholder: 'Email',
          rules: [],
          value: '',
          autocomplete: 'email',
          hidden: false,
        },
        password: {
          type: 'password',
          placeholder: 'Password',
          rules: ['presence'],
          value: '',
          autocomplete: 'current-password',
          hidden: false,
        },
      },
    }
  },
  props: {
    isLoading: {
      type: Boolean,
      required: true,
    },
    authProviders: {
      type: Array,
      required: true,
    },
    invitation: {
      type: Object,
      required: true,
    },
  },
  computed: {
    invitationFrom() {
      return this.invitation?.invited_by?.first_name || ''
    },
    invitationCompany() {
      return this.invitation.account.name
    },
    invitationEmail() {
      return this.invitation.user_invite?.email
    },
  },
  methods: {
    async validateFields() {
      const { fields } = this
      const { isValid, errors } = validateForm(fields)
      this.errors = errors
      if (isValid) {
        const payload = {
          email: fields.email.value || this.invitationEmail,
          password: fields.password.value,
        }
        this.$emit('submit-form', payload)
      }
    },
    clearErrors() {
      this.errors = clearFormErrors()
    },
  },
  mounted() {
    if (this.invitationEmail) {
      this.fields.email.hidden = true
    } else {
      this.fields.email.rules.push('presence')
    }
  },
}
</script>
<style lang="scss" scoped>
.business-login-form {
  &__form-control {
    width: 100%;
    margin-bottom: 10px;
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      background: var(--light-blue-gray);
    }
  }
  &__oauth {
    &__icon {
      fill: none !important;
    }
  }
  &__oauth {
    position: relative;
    width: 100%;
    margin: 90px 0 50px 0;
    padding: 20px 0 0 0;
    border-top: 1px solid var(--tundora-gray);
    &--separator {
      font-family: var(--font-bold);
      position: absolute;
      color: var(--white);
      top: -19px;
      width: 38px;
      height: 38px;
      left: 0;
      right: 0;
      margin: 0 auto;
      border-radius: 60px;
      background: var(--dark-brighter);
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  &__actions {
    text-align: center;
    &__group {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 20px;
    }
    &__submit-wrapper {
      margin-top: 0 !important;
    }
    a {
      color: var(--light-blue-gray);
      &:hover {
        color: var(--white);
      }
    }
  }
  &__with-email {
    color: var(--white);
    font-size: rem(16px);
    margin-bottom: 10px;
  }
  ::v-deep .ine-form__title {
    color: var(--white);
  }
  ::v-deep .ine-form__tagline {
    color: var(--light-blue-gray);
    margin-bottom: 50px;
  }
  ::v-deep .ine-form__submit-btn-wrapper {
    margin: 20px 0 0 auto;
  }
}
</style>
